import { DataTable } from "@/components/data-table/data-table.tsx";
import { columns } from "@/components/referrals/columns.tsx";
import { ExportReferralsButton } from "@/components/referrals/export-referrals-button.tsx";
import { Filters } from "@/components/referrals/filters.tsx";
import { useReferrals } from "@/lib/api/referrals.ts";
import { UserRole } from "@/lib/api/user.ts";
import { useDataTable } from "@/lib/hooks/use-data-table.ts";
import {
  createFileRoute,
  redirect,
  useRouter,
  useSearch,
} from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/referrals")({
  component: Referrals,
  beforeLoad: ({ context }) => {
    // Redirect to the home page if the user is not an admin
    if (
      context.user?.role !== UserRole.ADMIN &&
      context.user?.role !== UserRole.SUPERADMIN
    ) {
      throw redirect({ to: "/" });
    }
  },
});

function Referrals() {
  const router = useRouter();
  const { page, search, teenId, dateFrom, dateTo, per_page, type } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const currentPage = parseInt(page || "1", 10);
  const pageSize = parseInt(per_page || "20", 10);

  const { data, isPending } = useReferrals(
    currentPage,
    pageSize,
    search,
    teenId,
    dateFrom,
    dateTo,
    type,
  );

  const { table } = useDataTable({
    columns,
    data: data?.referrals || [],
    defaultPerPage: pageSize,
    pageCount: -1, // Always use -1 for infinite pagination
    filterFields: [],
  });

  const handlePageChange = (newPage: number) => {
    router.navigate({
      search: ((old: any) => ({
        ...old,
        page: newPage.toString(),
      })) as any,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    router.navigate({
      search: ((old: any) => ({
        ...old,
        per_page: newPageSize.toString(),
        page: "1", // Reset to first page when changing page size
      })) as any,
    });
  };

  return (
    <div className="space-y-4 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Referrals</h1>
        </div>
        <ExportReferralsButton
          filters={{ search, teenId, dateFrom, dateTo, type }}
        />
      </div>

      <DataTable
        table={table}
        getCanNextPage={() => {
          return data ? currentPage < data.totalPages : false;
        }}
        getCanPreviousPage={() => {
          return currentPage > 1;
        }}
        isPending={isPending}
        onPageChange={(newPageIndex: number) =>
          handlePageChange(newPageIndex + 1)
        }
        onPageSizeChange={handlePageSizeChange}
      >
        <Filters />
      </DataTable>
    </div>
  );
}
